import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import StickyHeader from './StickyHeader';
import Footer from './Footer';

const Layout = ({ children, topics = [], selectedTopic = null, onTopicSelect }) => {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
      {/* Sticky Header with Search */}
      <StickyHeader />

      {/* Topics Toggle Button for Mobile - Floating */}
      {topics.length > 0 && (
        <button
          onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
          className="lg:hidden fixed bottom-6 right-4 z-30 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all"
          aria-label="Browse Topics"
        >
          <Menu className="w-6 h-6" />
        </button>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Desktop */}
          {topics.length > 0 && (
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="bg-white rounded-xl shadow-md p-6 sticky top-20">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Topics</h2>
                <div className="space-y-2">
                  <button
                    onClick={() => onTopicSelect?.(null)}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                      !selectedTopic
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                    data-testid="topic-all"
                  >
                    All Topics
                  </button>
                  {topics.map((topic) => {
                    // Use backend slug if available, fallback to generating from name
                    const topicSlug = topic.slug || topic.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                    return (
                      <Link
                        key={topic.id}
                        to={`/topics/${topicSlug}`}
                        className={`w-full text-left px-4 py-2 rounded-lg transition-colors flex items-center justify-between ${
                          selectedTopic === topic.id
                          ? 'text-white'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                      style={{
                        backgroundColor: selectedTopic === topic.id ? topic.color : 'transparent'
                      }}
                      data-testid={`topic-${topicSlug}`}
                    >
                      <span className="truncate">{topic.name}</span>
                      <span className="text-xs opacity-75 ml-2">({topic.question_count})</span>
                    </Link>
                  );
                  })}
                </div>
              </div>
            </aside>
          )}

          {/* Mobile Sidebar Overlay */}
          {mobileSidebarOpen && topics.length > 0 && (
            <div className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-50" onClick={() => setMobileSidebarOpen(false)}>
              <div className="absolute inset-y-0 left-0 w-64 bg-white shadow-xl p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900">Topics</h2>
                  <button onClick={() => setMobileSidebarOpen(false)}>
                    <X className="w-6 h-6 text-gray-600" />
                  </button>
                </div>
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      onTopicSelect?.(null);
                      setMobileSidebarOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                      !selectedTopic
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    All Topics
                  </button>
                  {topics.map((topic) => (
                    <button
                      key={topic.id}
                      onClick={() => {
                        onTopicSelect?.(topic.id);
                        setMobileSidebarOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-colors flex items-center justify-between ${
                        selectedTopic === topic.id
                          ? 'text-white'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                      style={{
                        backgroundColor: selectedTopic === topic.id ? topic.color : 'transparent'
                      }}
                    >
                      <span className="truncate">{topic.name}</span>
                      <span className="text-xs opacity-75">({topic.question_count})</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {children}
          </main>
        </div>
        
        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default Layout;
